import {
  forwardRef,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import type { SupportHubSource } from "@b2bportal/core-self-serve";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { TranslationLanguage } from "@hopper-b2b/types";
import { useSupportChatButton } from "@tenant/self-serve";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./ContactSupportModal.module.scss";
import { useSupportHubTracking } from "./supportHubTracking";
import AvionHorizontalBlueEn from "@/assets/icons/ui/avion-horizontal-blue-en.svg?react";
import AvionHorizontalBlueFr from "@/assets/icons/ui/avion-horizontal-blue-fr.svg?react";
import { pathnameToEntryPointWithFallback } from "@/components/HtsAssistChatWidget/htsAssistEntryPoint";
import { pathnameToSupportContext } from "@/components/HtsAssistChatWidget/htsAssistSupportContext";
import { useIsHtsAssistEnabled } from "@/components/HtsAssistChatWidget/useIsHtsAssistEnabled";
import { ContactSupport } from "@/overrides/core-self-serve/ContactSupport/ContactSupport";
import { openHtsAssistChat } from "@/store/htsAssistEntryState";

export interface ContactSupportModalProps {
  className?: string;
  enableHtsAssistChoice?: boolean;
  /** What opened the modal, sent as `source` on `support_hub_viewed`. */
  source?: SupportHubSource;
  trigger?: ReactNode | false;
}

export interface ContactSupportModalHandle {
  open: () => void;
}

export const ContactSupportModal = forwardRef<
  ContactSupportModalHandle,
  ContactSupportModalProps
>(({ className, enableHtsAssistChoice = false, source, trigger }, ref) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"menu" | "contact" | "faq">("menu");
  const {
    trackViewed,
    trackTappedManageBooking,
    trackTappedFaq,
    trackTappedContactSupport,
    trackDismissed,
  } = useSupportHubTracking();

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setOpen(true);
        trackViewed(source);
      },
    }),
    [source, trackViewed]
  );

  const { t, brand, locale } = useI18nContext();
  const location = useLocation();
  const dispatch = useDispatch();
  const { context: chatContext, handlers: chatHandlers } =
    useSupportChatButton();
  const isHtsAssistEnabled = useIsHtsAssistEnabled();
  const isFrench =
    locale === TranslationLanguage.fr ||
    locale === TranslationLanguage["fr-CA"];
  const AvionLogo = isFrench ? AvionHorizontalBlueFr : AvionHorizontalBlueEn;
  const navigate = useNavigate();
  const { helpCenterBaseUrl, contactSupportUrl } = brand.urls;
  const [block, cn] = useModuleBEM(styles, "ContactSupportModal");
  const supportContext = useMemo(
    () => pathnameToSupportContext(location.pathname),
    [location.pathname]
  );
  const showHtsAssistChoice =
    enableHtsAssistChoice && isHtsAssistEnabled && supportContext != null;

  const handleStartHtsAssist = useCallback(() => {
    if (!supportContext) return;

    if (!chatContext.hasActiveChatSession) {
      dispatch(
        openHtsAssistChat({
          entryPoint: pathnameToEntryPointWithFallback(location.pathname),
          supportContext,
        })
      );
    }

    setOpen(false);
    chatHandlers.toggleChat();
  }, [
    chatContext.hasActiveChatSession,
    chatHandlers,
    dispatch,
    location.pathname,
    supportContext,
  ]);

  // Only fires for user-driven open/close (trigger, overlay, Escape, close
  // button). Programmatic `setOpen(false)` after an action does not count as a
  // dismissal.
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    if (isOpen) {
      trackViewed(source);
    } else {
      trackDismissed(view);
      setView("menu");
    }
  };

  const handleManageBookingClick = () => {
    trackTappedManageBooking();
    setOpen(false);
    navigate(TRIPS_PATH);
  };

  const handleFaqClick = () => {
    if (helpCenterBaseUrl == null) {
      return;
    }

    trackTappedFaq();
    setView("faq");
  };

  const handleContactSupportClick = () => {
    trackTappedContactSupport();
    setView("contact");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger !== false && (
        <DialogTrigger className={className}>
          {trigger ?? t("selfServe.contactSupport")}
        </DialogTrigger>
      )}
      <DialogContent
        className={clsx(
          cn("dialog"),
          showHtsAssistChoice && cn("dialog-assist")
        )}
        showCloseButton={false}
        showHeaderBottomBorder={false}
        header={
          <div className={cn("header")}>
            {view === "contact" || view === "faq" ? (
              <button
                type="button"
                className={cn("header-button")}
                onClick={() => setView("menu")}
                aria-label={t("back")}
              >
                <Icon iconName={IconNameEnum.backIcon} />
              </button>
            ) : (
              <div className={cn("header-spacer")} />
            )}

            <div className={cn("header-spacer")} />

            <DialogClose asChild>
              <button
                type="button"
                className={cn("header-button")}
                aria-label={t("close.button")}
              >
                <Icon iconName={IconNameEnum.close} />
              </button>
            </DialogClose>
          </div>
        }
      >
        <div className={block}>
          {showHtsAssistChoice && view === "menu" ? (
            <div className={cn("assist-choice")}>
              <DialogTitle className={cn("sr-only")}>
                {t("core-self-serve.contactSupport.title")}
              </DialogTitle>
              <ContactSupport startChat={handleStartHtsAssist} />
              {helpCenterBaseUrl != null && (
                <p className={cn("assist-footer")}>
                  <Trans
                    i18nKey="core-self-serve.contactSupport.contactFAQ"
                    components={{
                      faqlink: (
                        <button
                          type="button"
                          aria-label={t(
                            "core-self-serve.contactSupport.faqTitle"
                          )}
                          onClick={handleFaqClick}
                        />
                      ),
                    }}
                  />
                </p>
              )}
            </div>
          ) : (
            <>
              <div className={cn("brand")}>
                <AvionLogo
                  className={cn("brand-logo")}
                  width="auto"
                  height={20}
                  aria-hidden="true"
                />
              </div>
              {view === "menu" ? (
                <div className={cn("menu")}>
                  <DialogTitle className={cn("title")}>
                    {t("core-self-serve.contactSupport.menuTitle")}
                  </DialogTitle>

                  <div className={cn("actions")}>
                    <button
                      type="button"
                      className={cn("action-card")}
                      onClick={handleManageBookingClick}
                    >
                      <div className={cn("action-icon")}>
                        <Icon iconName={IconNameEnum.trips} />
                      </div>
                      <div className={cn("action-content")}>
                        <span className={cn("action-title")}>
                          {t("core-self-serve.contactSupport.bookingTitle")}
                        </span>
                        <span className={cn("action-subtitle")}>
                          {t("core-self-serve.contactSupport.bookingSubtitle")}
                        </span>
                      </div>
                      <Icon
                        className={cn("action-arrow")}
                        iconName={IconNameEnum.chevronRight}
                      />
                    </button>

                    {helpCenterBaseUrl != null && (
                      <button
                        type="button"
                        className={cn("action-card")}
                        onClick={handleFaqClick}
                      >
                        <div className={cn("action-icon")}>
                          <Icon iconName={IconNameEnum.questionCircle} />
                        </div>
                        <div className={cn("action-content")}>
                          <span className={cn("action-title")}>
                            {t("core-self-serve.contactSupport.faqTitle")}
                          </span>
                          <span className={cn("action-subtitle")}>
                            {t("core-self-serve.contactSupport.faqSubtitle")}
                          </span>
                        </div>
                        <Icon
                          className={cn("action-arrow")}
                          iconName={IconNameEnum.chevronRight}
                        />
                      </button>
                    )}

                    <button
                      type="button"
                      className={cn("action-card")}
                      onClick={handleContactSupportClick}
                    >
                      <div className={cn("action-icon")}>
                        <Icon iconName={IconNameEnum.phone} />
                      </div>
                      <div className={cn("action-content")}>
                        <span className={cn("action-title")}>
                          {t("core-self-serve.contactSupport.directTitle")}
                        </span>
                        <span className={cn("action-subtitle")}>
                          {t("core-self-serve.contactSupport.directSubtitle")}
                        </span>
                      </div>
                      <Icon
                        className={cn("action-arrow")}
                        iconName={IconNameEnum.chevronRight}
                      />
                    </button>
                  </div>
                </div>
              ) : view === "contact" ? (
                <>
                  <DialogTitle className={cn("sr-only")}>
                    {t("core-self-serve.contactSupport.title")}
                  </DialogTitle>
                  <div className={cn("iframe-container")}>
                    <iframe
                      className={cn("iframe")}
                      src={contactSupportUrl}
                      title={t("core-self-serve.contactSupport.title")}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </>
              ) : (
                <>
                  <DialogTitle className={cn("sr-only")}>
                    {t("core-self-serve.contactSupport.faqTitle")}
                  </DialogTitle>
                  <div className={cn("iframe-container")}>
                    <iframe
                      className={cn("iframe")}
                      src={helpCenterBaseUrl}
                      title={t("core-self-serve.contactSupport.faqTitle")}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

ContactSupportModal.displayName = "ContactSupportModal";
